import React, { useContext, useState, useCallback, useEffect } from 'react';
import { Key, Search, Filter, RefreshCw, Copy, ExternalLink, Globe, CheckCircle, XCircle, CreditCard, Check, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/Badge';
import ExportActionBtn from '../../components/ui/ExportActionBtn';
import { CredentialsApi, TeamsApi, StealerLeaksListOwnerEnum } from 'api-client';
import { MonitoredDomain, PaginatedStealerLeakList, StealerLeak, Status111Enum, StealerLeakSeverityEnum, TwoFaStatusEnum, ValidityEnum } from 'api-client/models';
import { FilterSelect } from '@/components/findings/filters/FilterSelect';
import { useTeams } from '../../contexts/TeamsContext';
import { AuthContext } from '../../contexts/AuthContext';
import { getApiConfiguration } from '../../api/utils';
import { TablePagination } from '../../components/ui/TablePagination';
import { StealerLeaksFilters } from '../../components/data-protection/StealerLeaksFilters';
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { AddAssetButton } from '@/components/assets/AssetActionButtons';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';

/**
 * Props for the StealerLeaksList component
 */
interface StealerLeaksListProps {
  /** Stealer leaks data to display (optional, will fetch from API if not provided) */
  leaks?: StealerLeak[];
  /** Current page number for pagination (optional) */
  currentPage?: number;
  /** Function to handle page changes (optional) */
  onPageChange?: (page: number) => void;
  /** Total number of leaks for pagination (optional) */
  totalLeaks?: number;
  /** Number of items per page (optional) */
  itemsPerPage?: number;
  /** Pagination information object (optional) */
  paginationInfo?: {
    count: number;
    next: string | null;
    previous: string | null;
  };
  /** Whether to show the header section (optional) */
  showHeader?: boolean;
  /** Whether to show the filters section (optional) */
  showFilters?: boolean;
}

/**
 * StealerLeaksList component that can either use provided data or fetch from API
 */
// Domain validation regex pattern
const domainRegex = /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9][a-z0-9-]{0,61}[a-z0-9]$/i;

const StealerLeaksList: React.FC<StealerLeaksListProps> = ({
  leaks: providedLeaks,
  currentPage: providedCurrentPage,
  onPageChange: providedOnPageChange,
  totalLeaks,
  itemsPerPage = 10,
  paginationInfo: providedPaginationInfo,
  showHeader = true,
  showFilters = true,
}) => {
  const { currentTeam } = useTeams();
  const { token } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [selectedDomain, setSelectedDomain] = useState(searchParams.get('domainName') || 'all');
  const [selectedService, setSelectedService] = useState(searchParams.get('serviceName') || 'all');
  const [selectedOwner, setSelectedOwner] = useState(searchParams.get('owner') || 'all');
  const [isMobile, setIsMobile] = useState(searchParams.get('isMobile') || 'all');
  const [twoFaStatus, setTwoFaStatus] = useState(searchParams.get('twoFaStatus') || 'all');
  const [severity, setSeverity] = useState(searchParams.get('severity') || 'all');
  const [validity, setValidity] = useState(searchParams.get('validity') || 'all');
  const [status, setStatus] = useState(searchParams.get('status') || 'all');
  const [read, setRead] = useState(searchParams.get('read') || 'all');
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page') || '1', 10));
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [leaks, setLeaks] = useState<StealerLeak[]>(providedLeaks || []);
  const [paginationInfo, setPaginationInfo] = useState<{
    count: number;
    next: string | null;
    previous: string | null;
  }>(providedPaginationInfo || { count: totalLeaks || 0, next: null, previous: null });
  // Track which passwords are currently visible
  const [visiblePasswords, setVisiblePasswords] = useState<Record<number, boolean>>({});
  
  // State for tracking when an update is in progress
  const [isUpdating, setIsUpdating] = useState(false);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [domainInput, setDomainInput] = useState<string>('');
  const [domainDescription, setDomainDescription] = useState<string>('');
  const [inputError, setInputError] = useState<string>('');
  const [monitoredDomains, setMonitoredDomains] = useState<MonitoredDomain[]>([]);
  
  // Credit information
  // const [creditInfo, setCreditInfo] = useState<{ available: number; total: number }>({ available: 0, total: 100 });

  // Handle page changes, either using provided handler or internal state
  const handlePageChange = (page: number) => {
    if (providedOnPageChange) {
      providedOnPageChange(page);
    } else {
      setCurrentPage(page);
    }

    const url = buildUrlWithParams({
      page,
      search: searchQuery,
      domainName: selectedDomain,
      serviceName: selectedService,
      owner: selectedOwner,
      isMobile,
      twoFaStatus,
      severity,
      validity,
      status,
      read,
    });
  
    // Navigate to the new URL
    navigate(url, {
      state: { pageChange: true, timestamp: Date.now() }
    });
  };

  const handleReset = () => {
    setSearchQuery('');
    setSelectedDomain('all');
    setSelectedService('all');
    setSelectedOwner('all');
    setIsMobile('all');
    setTwoFaStatus('all');
    setSeverity('all');
    setValidity('all');
    setStatus('all');
    setRead('all');
    setCurrentPage(1);

    // Reset URL params
    navigate(location.pathname, { replace: true });
    fetchLeaks();
  };

  useEffect(() => {
    // Only update if the change is from navigation (not from local state changes)
    const pageParam = searchParams.get('page');
    const searchParam = searchParams.get('search');
    const domainParam = searchParams.get('domainName');
    const serviceParam = searchParams.get('serviceName');
    const ownerParam = searchParams.get('owner');
    const isMobileParam = searchParams.get('isMobile');
    const twoFaStatusParam = searchParams.get('twoFaStatus');
    const severityParam = searchParams.get('severity');
    const validityParam = searchParams.get('validity');
    const statusParam = searchParams.get('status');
    const readParam = searchParams.get('read');
    
    // Update page if it changed
    if (pageParam) {
      const newPage = parseInt(pageParam, 10);
      if (newPage !== currentPage) {
        setCurrentPage(newPage);
      }
    } else if (currentPage !== 1) {
      // If page param is removed, reset to page 1
      setCurrentPage(1);
    }
    
    // Update search query if it changed
    if (searchParam !== searchQuery) {
      setSearchQuery(searchParam || '');
    }
    
    // Update domain if it changed
    if (domainParam !== selectedDomain) {
      setSelectedDomain(domainParam || '');
    }
    
    // Update service if it changed
    if (serviceParam !== selectedService) {
      setSelectedService(serviceParam || '');
    }
    
    // Update owner if it changed
    if (ownerParam !== selectedOwner) {
      setSelectedOwner(ownerParam || '');
    }
    
    // Update isMobile if it changed
    if (isMobileParam !== isMobile) {
      setIsMobile(isMobileParam || '');
    }
    
    // Update twoFaStatus if it changed
    if (twoFaStatusParam !== twoFaStatus) {
      setTwoFaStatus(twoFaStatusParam || '');
    }
    
    // Update severity if it changed
    if (severityParam !== severity) {
      setSeverity(severityParam || '');
    }
    
    // Update validity if it changed
    if (validityParam !== validity) {
      setValidity(validityParam || '');
    }
    
    // Update status if it changed
    if (statusParam !== status) {
      setStatus(statusParam || '');
    }
    
    // Update read status if it changed
    if (readParam !== read) {
      setRead(readParam || '');
    }
  }, [searchParams]);

  const buildUrlWithParams = (params: Record<string, any>) => {
    const urlParams = new URLSearchParams();
    
    // Add search query if present
    if (params.search) {
      urlParams.append('search', params.search);
    }
    
    // Add domain filter if selected
    if (params.domainName && params.domainName !== 'all') {
      urlParams.append('domainName', params.domainName);
    }
    
    // Add service filter if selected
    if (params.serviceName && params.serviceName !== 'all') {
      urlParams.append('serviceName', params.serviceName);
    }
    
    // Add owner filter if selected
    if (params.owner && params.owner !== 'all') {
      urlParams.append('owner', params.owner);
    }
    
    // Add isMobile filter if selected
    if (params.isMobile && params.isMobile !== 'all') {
      urlParams.append('isMobile', params.isMobile);
    }
    
    // Add twoFaStatus filter if selected
    if (params.twoFaStatus && params.twoFaStatus !== 'all') {
      urlParams.append('twoFaStatus', params.twoFaStatus);
    }
    
    // Add severity filter if selected
    if (params.severity && params.severity !== 'all') {
      urlParams.append('severity', params.severity);
    }
    
    // Add validity filter if selected
    if (params.validity && params.validity !== 'all') {
      urlParams.append('validity', params.validity);
    }
    
    // Add status filter if selected
    if (params.status && params.status !== 'all') {
      urlParams.append('status', params.status);
    }
    
    // Add read filter if selected
    if (params.read && params.read !== 'all') {
      urlParams.append('read', params.read);
    }
    
    // Add page if not default
    if (params.page && params.page > 1) {
      urlParams.append('page', params.page.toString());
    }
    
    return `${location.pathname}?${urlParams.toString()}`;
  };
  
  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    setCurrentPage(1); // Reset to first page when search changes
    
    // Update URL with the new search query
    const url = buildUrlWithParams({
      search: query,
      domainName: selectedDomain,
      serviceName: selectedService,
      owner: selectedOwner,
      isMobile,
      twoFaStatus,
      severity,
      validity,
      status,
      read,
      page: 1,
    });
    
    // Navigate with a unique state to ensure history entries are distinct
    navigate(url, { state: { timestamp: Date.now() } });
    
    // Fetch leaks with new search query
    fetchLeaks();
  };

  const handleDomainChange = (domain: string) => {
    setSelectedDomain(domain);
    setCurrentPage(1); // Reset to first page when filter changes
    
    // Update URL with the new filter
    const url = buildUrlWithParams({
      search: searchQuery,
      domainName: domain,
      serviceName: selectedService,
      owner: selectedOwner,
      isMobile,
      twoFaStatus,
      severity,
      validity,
      status,
      read,
      page: 1,
    });
    
    // Navigate with a unique state to ensure history entries are distinct
    navigate(url, { state: { timestamp: Date.now() } });
    
    // Fetch leaks with new filter
    fetchLeaks();
  };

  const handleServiceChange = (service: string) => {
    setSelectedService(service);
    setCurrentPage(1); // Reset to first page when filter changes
    
    // Update URL with the new filter
    const url = buildUrlWithParams({
      search: searchQuery,
      domainName: selectedDomain,
      serviceName: service,
      owner: selectedOwner,
      isMobile,
      twoFaStatus,
      severity,
      validity,
      status,
      read,
      page: 1,
    });
    
    // Navigate with a unique state to ensure history entries are distinct
    navigate(url, { state: { timestamp: Date.now() } });
    
    // Fetch leaks with new filter
    fetchLeaks();
  };

  const handleOwnerChange = (owner: string) => {
    setSelectedOwner(owner);
    setCurrentPage(1); // Reset to first page when filter changes
    
    // Update URL with the new filter
    const url = buildUrlWithParams({
      search: searchQuery,
      domainName: selectedDomain,
      serviceName: selectedService,
      owner,
      isMobile,
      twoFaStatus,
      severity,
      validity,
      status,
      read,
      page: 1,
    });
    
    // Navigate with a unique state to ensure history entries are distinct
    navigate(url, { state: { timestamp: Date.now() } });
    
    // Fetch leaks with new filter
    fetchLeaks();
  };

  const handleIsMobileChange = (isMobileValue: string) => {
    setIsMobile(isMobileValue);
    setCurrentPage(1); // Reset to first page when filter changes
    
    // Update URL with the new filter
    const url = buildUrlWithParams({
      search: searchQuery,
      domainName: selectedDomain,
      serviceName: selectedService,
      owner: selectedOwner,
      isMobile: isMobileValue,
      twoFaStatus,
      severity,
      validity,
      status,
      read,
      page: 1,
    });
    
    // Navigate with a unique state to ensure history entries are distinct
    navigate(url, { state: { timestamp: Date.now() } });
    
    // Fetch leaks with new filter
    fetchLeaks();
  };

  const handleTwoFaStatusChange = (twoFaStatusValue: string) => {
    setTwoFaStatus(twoFaStatusValue);
    setCurrentPage(1); // Reset to first page when filter changes
    
    // Update URL with the new filter
    const url = buildUrlWithParams({
      search: searchQuery,
      domainName: selectedDomain,
      serviceName: selectedService,
      owner: selectedOwner,
      isMobile,
      twoFaStatus: twoFaStatusValue,
      severity,
      validity,
      status,
      read,
      page: 1,
    });
    
    // Navigate with a unique state to ensure history entries are distinct
    navigate(url, { state: { timestamp: Date.now() } });
    
    // Fetch leaks with new filter
    fetchLeaks();
  };

  const handleSeverityChange = (severityValue: string) => {
    setSeverity(severityValue);
    setCurrentPage(1); // Reset to first page when filter changes
    
    // Update URL with the new filter
    const url = buildUrlWithParams({
      search: searchQuery,
      domainName: selectedDomain,
      serviceName: selectedService,
      owner: selectedOwner,
      isMobile,
      twoFaStatus,
      severity: severityValue,
      validity,
      status,
      read,
      page: 1,
    });
    
    // Navigate with a unique state to ensure history entries are distinct
    navigate(url, { state: { timestamp: Date.now() } });
    
    // Fetch leaks with new filter
    fetchLeaks();
  };

  const handleValidityChange = (validityValue: string) => {
    setValidity(validityValue);
    setCurrentPage(1); // Reset to first page when filter changes
    
    // Update URL with the new filter
    const url = buildUrlWithParams({
      search: searchQuery,
      domainName: selectedDomain,
      serviceName: selectedService,
      owner: selectedOwner,
      isMobile,
      twoFaStatus,
      severity,
      validity: validityValue,
      status,
      read,
      page: 1,
    });
    
    // Navigate with a unique state to ensure history entries are distinct
    navigate(url, { state: { timestamp: Date.now() } });
    
    // Fetch leaks with new filter
    fetchLeaks();
  };

  const handleStatusChange = (statusValue: string) => {
    setStatus(statusValue);
    setCurrentPage(1); // Reset to first page when filter changes
    
    // Update URL with the new filter
    const url = buildUrlWithParams({
      search: searchQuery,
      domainName: selectedDomain,
      serviceName: selectedService,
      owner: selectedOwner,
      isMobile,
      twoFaStatus,
      severity,
      validity,
      status: statusValue,
      read,
      page: 1,
    });
    
    // Navigate with a unique state to ensure history entries are distinct
    navigate(url, { state: { timestamp: Date.now() } });
    
    // Fetch leaks with new filter
    fetchLeaks();
  };

  const handleReadChange = (readValue: string) => {
    setRead(readValue);
    setCurrentPage(1); // Reset to first page when filter changes
    
    // Update URL with the new filter
    const url = buildUrlWithParams({
      search: searchQuery,
      domainName: selectedDomain,
      serviceName: selectedService,
      owner: selectedOwner,
      isMobile,
      twoFaStatus,
      severity,
      validity,
      status,
      read: readValue,
      page: 1,
    });
    
    // Navigate with a unique state to ensure history entries are distinct
    navigate(url, { state: { timestamp: Date.now() } });
    
    // Fetch leaks with new filter
    fetchLeaks();
  };

  // Handle attribute change
  const handleAttributeChange = async (leakId: number, attribute: string, value: any) => {
    if (!currentTeam?.slug || !token) return;
    
    // Check if user is admin
    if (!currentTeam.isAdmin) {
      toast.error('Only administrators can modify stealer leak attributes');
      return;
    }
    
    setIsUpdating(true);
    try {
      const api = new CredentialsApi(getApiConfiguration(token));
      await api.credentialsStealerLeaksPartialUpdate({
        id: leakId.toString(),
        teamSlug: currentTeam.slug,
        patchedStealerLeak: {
          [attribute]: value
        }
      });
      
      // Update local state
      setLeaks(prev => prev.map(leak => 
        leak.id === leakId ? { ...leak, [attribute]: value } : leak
      ));
      
      toast.success(`Updated ${attribute} successfully`);
    } catch (err) {
      console.error(`Error updating ${attribute}:`, err);
      toast.error(`Failed to update ${attribute}`);
    } finally {
      setIsUpdating(false);
    }
  };
  
  // Get 2FA status color
  const get2FAStatusColor = (twoFaStatus?: TwoFaStatusEnum) => {
    switch (twoFaStatus) {
      case TwoFaStatusEnum.Enabled:
        return 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20';
      case TwoFaStatusEnum.Disabled:
        return 'bg-red-500/10 text-red-400 border-red-500/20';
      case TwoFaStatusEnum.None:
      default:
        return 'bg-gray-500/10 text-gray-400 border-gray-500/20';
    }
  };
  
  // Get severity color
  const getSeverityColor = (severity?: StealerLeakSeverityEnum) => {
    switch (severity) {
      case StealerLeakSeverityEnum.Critical:
        return 'bg-red-500/10 text-red-400 border-red-500/20';
      case StealerLeakSeverityEnum.High:
        return 'bg-orange-500/10 text-orange-400 border-orange-500/20';
      case StealerLeakSeverityEnum.Medium:
        return 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20';
      case StealerLeakSeverityEnum.Low:
        return 'bg-blue-500/10 text-blue-400 border-blue-500/20';
      case StealerLeakSeverityEnum.Info:
      default:
        return 'bg-gray-500/10 text-gray-400 border-gray-500/20';
    }
  };
  
  // Get status color
  const getStatusColor = (status?: Status111Enum) => {
    switch (status) {
      case Status111Enum.Open:
        return 'bg-blue-500/10 text-blue-400 border-blue-500/20';
      case Status111Enum.InProgress:
        return 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20';
      case Status111Enum.Resolved:
        return 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20';
      case Status111Enum.FalsePositive:
        return 'bg-purple-500/10 text-purple-400 border-purple-500/20';
      case Status111Enum.Duplicate:
        return 'bg-indigo-500/10 text-indigo-400 border-indigo-500/20';
      case Status111Enum.Retest:
        return 'bg-orange-500/10 text-orange-400 border-orange-500/20';
      default:
        return 'bg-gray-500/10 text-gray-400 border-gray-500/20';
    }
  };
  
  // Get validity color
  const getValidityColor = (validity?: ValidityEnum) => {
    switch (validity) {
      case ValidityEnum.Valid:
        return 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20';
      case ValidityEnum.Invalid:
        return 'bg-red-500/10 text-red-400 border-red-500/20';
      case ValidityEnum.New:
      default:
        return 'bg-blue-500/10 text-blue-400 border-blue-500/20';
    }
  };

  const fetchLeaks = useCallback(async () => {
    // Skip API call if data is provided externally
    if (providedLeaks) return;
    
    if (!currentTeam?.slug || !token) return;

    setIsLoading(true);
    setError(null);

    try {
      const api = new CredentialsApi(getApiConfiguration(token));
      const params: any = {
        teamSlug: currentTeam.slug,
        page: currentPage,
        pageSize: itemsPerPage,
      };

      // Add search query if present
      if (searchQuery) {
        params.search = searchQuery;
      }

      // Add domain filter if selected
      if (selectedDomain && selectedDomain !== 'all') {
        params.domainName = selectedDomain;
      }

      // Add service filter if selected
      if (selectedService && selectedService !== 'all') {
        params.serviceName = selectedService;
      }

      // Add owner filter if selected
      if (selectedOwner && selectedOwner !== 'all') {
        params.owner = selectedOwner;
      }

      // Add isMobile filter if selected
      if (isMobile && isMobile !== 'all') {
        params.isMobile = isMobile === 'true';
      }

      // Add twoFaStatus filter if selected
      if (twoFaStatus && twoFaStatus !== 'all') {
        params.twoFaStatus = twoFaStatus;
      }

      // Add severity filter if selected
      if (severity && severity !== 'all') {
        params.severity = severity;
      }

      // Add validity filter if selected
      if (validity && validity !== 'all') {
        params.validity = validity;
      }

      // Add status filter if selected
      if (status && status !== 'all') {
        params.status = status;
      }
      
      // Add read filter if selected
      if (read && read !== 'all') {
        params.readed = read === 'true';
      }

      const response = await api.stealerLeaksList(params);
      setLeaks(response.results);
      setPaginationInfo({
        count: response.count,
        next: response.next || null,
        previous: response.previous || null
      });
    } catch (err) {
      console.error('Error fetching stealer leaks:', err);
      setError('Failed to load stealer leaks');
    } finally {
      setIsLoading(false);
    }
  }, [currentTeam?.slug, token, currentPage, searchQuery, selectedDomain, selectedService, selectedOwner, isMobile, twoFaStatus, severity, validity, status, read, itemsPerPage, providedLeaks]);

  const fetchAllLeaks = async(totalLeaksCount: number) => {
    if (!currentTeam?.slug || !token) return;

    try {
      const api = new CredentialsApi(getApiConfiguration(token));
      const params: any = {
        teamSlug: currentTeam.slug,
        pageSize: totalLeaksCount,
      };

      // Add search query if present
      if (searchQuery) {
        params.search = searchQuery;
      }

      // Add domain filter if selected
      if (selectedDomain && selectedDomain !== 'all') {
        params.domainName = selectedDomain;
      }

      // Add service filter if selected
      if (selectedService && selectedService !== 'all') {
        params.serviceName = selectedService;
      }

      // Add owner filter if selected
      if (selectedOwner && selectedOwner !== 'all') {
        params.owner = selectedOwner;
      }

      // Add isMobile filter if selected
      if (isMobile && isMobile !== 'all') {
        params.isMobile = isMobile === 'true';
      }

      // Add twoFaStatus filter if selected
      if (twoFaStatus && twoFaStatus !== 'all') {
        params.twoFaStatus = twoFaStatus;
      }

      // Add severity filter if selected
      if (severity && severity !== 'all') {
        params.severity = severity;
      }

      // Add validity filter if selected
      if (validity && validity !== 'all') {
        params.validity = validity;
      }

      // Add status filter if selected
      if (status && status !== 'all') {
        params.status = status;
      }
      
      // Add read filter if selected
      if (read && read !== 'all') {
        params.readed = read === 'true';
      }
      const response = await api.stealerLeaksList(params);
      return response.results;
    } catch (err) {
      toast.error('Failed to Download all leaks');
      return [];
    }
  }

  // const addMonitoredDomain = async (domainName: string, description: string = '') => {
  //   if (!currentTeam?.slug || !token || !domainName) return;

  //   setIsLoading(true);
  //   setError(null);

  //   try {
  //     const api = new CredentialsApi(getApiConfiguration(token));
  //     const response = await api.monitoredDomainsCreate({
  //       teamSlug: currentTeam.slug,
  //       monitoredDomain: {
  //         domainName: domainName,
  //         isActive: true,
  //         tagList: "",
  //         createdAt: new Date(),
  //         updatedAt: new Date(),
  //         tags: "",
  //         description: description
  //       }
  //     });
  //     // Refresh the list after adding
  //     fetchMonitoredDomains();
  //     toast.success(`Domain ${domainName} added successfully`);
  //   } catch (err: any) {
  //     console.error('Error adding monitored domain:', err);
  //     setError('Failed to add monitored domain');
  //     toast.error(err?.message || 'Failed to add monitored domain');
  //   } finally {
  //     setIsLoading(false);
  //     setIsModalOpen(false);
  //     setDomainInput('');
  //     setInputError('');
  //     setDomainDescription('');
  //   }
  // };

  // const fetchMonitoredDomains = useCallback(async () => {
  //   if (!currentTeam?.slug || !token) return;

  //   setIsLoading(true);
  //   setError(null);

  //   try {
  //     const api = new CredentialsApi(getApiConfiguration(token));
  //     const response = await api.monitoredDomainsList({
  //       teamSlug: currentTeam.slug,
  //     });
  //     setMonitoredDomains(response.results);
  //   } catch (err: any) {
  //     console.error('Error fetching monitored domains:', err);
  //     setError('Failed to load monitored domains');
  //     toast.error(err?.message || 'Failed to load monitored domains');
  //   } finally {
  //     setIsLoading(false);
  //   }
  // }, [currentTeam?.slug, token]);

  // const fetchCreditInfo = useCallback(async () => {
  //   if (!currentTeam?.slug || !token) return;
    
  //   if (currentTeam && typeof currentTeam.stealerLeaksCredits === 'number') {
  //     setCreditInfo({ 
  //       available: currentTeam.stealerLeaksCredits, 
  //       total: 100 
  //     });
  //     return;
  //   }
    
  //   try {
  //     setCreditInfo({
  //       available: 0,
  //       total: 100
  //     });
  //   } catch (err: any) {
  //     console.error('Error handling credit information:', err);
  //   }
  // }, [currentTeam, token]);

  useEffect(() => {
    if (providedLeaks) {
      setLeaks(providedLeaks);
    }
    if (providedCurrentPage) {
      setCurrentPage(providedCurrentPage);
    }
    if (providedPaginationInfo) {
      setPaginationInfo(providedPaginationInfo);
    } else if (totalLeaks !== undefined) {
      setPaginationInfo(prev => ({ ...prev, count: totalLeaks }));
    }
  }, [providedLeaks, providedCurrentPage, providedPaginationInfo, totalLeaks]);

  useEffect(() => {
    if (!providedLeaks) {
      fetchLeaks();
      // fetchMonitoredDomains();
      // fetchCreditInfo();
    }
  }, [
    fetchLeaks,
    providedLeaks,
    // fetchMonitoredDomains,
    // fetchCreditInfo
  ]);

  return (
    <div className="space-y-6">
      {showHeader && (
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Key className="h-8 w-8 text-blue-500" />
            <div>
              <h1 className="text-2xl font-bold text-white">Stealer Leaks</h1>
              <p className="text-sm text-gray-400">Comprehensive list of detected stealer leaks</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <ExportActionBtn
              fetchAllData={() => fetchAllLeaks(paginationInfo.count)}
              filename="stealer_leaks"
              disabled={leaks?.length === 0}
            />
            {/* <Button
              onClick={fetchLeaks}
              disabled={isLoading || !!providedLeaks}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              {isLoading ? 'Refreshing...' : 'Refresh'}
            </Button> */}
            {/* <AddAssetButton
              onClick={() => {
                setIsModalOpen(true);
                setDomainInput('');
                setInputError('');
              }}
              label="Add Stealer Leak"
              disabled={false}
            /> */}
          </div>
        </div>
      )}

      {/* <div className="mb-6">
        <div className="flex items-center mb-4">
          <Globe className="h-5 w-5 text-blue-500 mr-2" />
          <h2 className="text-xl font-semibold text-white">Monitored Domains</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          <div className="bg-gradient-to-br from-blue-900/50 to-purple-900/50 rounded-xl p-4 backdrop-blur-sm border border-blue-800/50 transition-all hover:shadow-md hover:shadow-blue-500/10">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-300">Stealer Leaks Credits</span>
                <div className="p-1.5 rounded-lg bg-blue-500/10">
                  <CreditCard className="h-4 w-4 text-blue-400" />
                </div>
              </div>
              <div className="text-3xl font-bold text-white">{creditInfo.available}</div>
            </div>
          </div>
          {monitoredDomains.length > 0 && monitoredDomains.map((domain) => (
            <div
              key={domain.id}
              className={`bg-[#0F1629]/50 rounded-xl p-4 backdrop-blur-sm border border-gray-800/50 transition-all hover:border-blue-500/30 hover:shadow-md hover:shadow-blue-500/10 cursor-pointer ${selectedDomain === domain.domainName ? 'border-blue-500/30' : ''}`}
              onClick={() => {
                searchParams.set('domainName', domain.domainName);
                setSelectedDomain(domain.domainName);
                navigate({
                  pathname: location.pathname,
                  search: searchParams.toString()
                });
              }}
            >
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="text-lg font-bold text-white truncate" title={domain.domainName}>
                    {domain.domainName}
                  </div>
                  <Badge
                    variant={domain.isActive ? "success" : "destructive"}
                  >
                    {domain.isActive ? 'Monitoring' : 'Paused'}
                  </Badge>
                </div>
                {domain.description && (
                  <div className="mt-2 text-sm text-gray-400 line-clamp-2" title={domain.description}>
                    {domain.description}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div> */}

      {showFilters && (
        <StealerLeaksFilters
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
          selectedDomain={selectedDomain}
          onDomainChange={handleDomainChange}
          selectedService={selectedService}
          onServiceChange={handleServiceChange}
          selectedOwner={selectedOwner}
          onOwnerChange={handleOwnerChange}
          isMobile={isMobile}
          onMobileChange={handleIsMobileChange}
          twoFaStatus={twoFaStatus}
          onTwoFaStatusChange={handleTwoFaStatusChange}
          severity={severity}
          onSeverityChange={handleSeverityChange}
          validity={validity}
          onValidityChange={handleValidityChange}
          status={status}
          onStatusChange={handleStatusChange}
          read={read}
          onReadChange={handleReadChange}
          onReset={handleReset}
        />
      )}

      {/* Leaks Table */}
      <div className="bg-[#0F1629] rounded-xl border border-gray-800/50 overflow-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-800">
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Identifier</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Password</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">URL</th>
              {/* <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Domain</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Service</th> */}
              {/* <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Type</th> */}
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Owner</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">2FA Status</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Severity</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Status</th>
              {/* <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Read Status</th> */}
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Validity</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Created At</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800">
            {isLoading ? (
              <tr>
                <td colSpan={10} className="px-6 py-4 text-center text-gray-400">
                  Loading...
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={8} className="px-6 py-4 text-center text-red-400">
                  {error}
                </td>
              </tr>
            ) : leaks.length === 0 ? (
              <tr>
                <td colSpan={10} className="px-6 py-4 text-center text-gray-400">
                  No stealer leaks found
                </td>
              </tr>
            ) : (
              leaks.map((leak) => (
                <tr key={leak.id} className={`hover:bg-gray-900/30 ${leak?.readed ? 'bg-emerald-500/10' : ''}`}>
                  <td
                    className="px-6 py-4 text-sm text-gray-300 cursor-pointer"
                    onClick={() => {
                      navigate(`/data-protection/stealer-leaks/${leak.id}`);
                    }}
                  >
                    {leak.identifier}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-300">
                    <div className="flex items-center space-x-2">
                      <span
                        className="font-mono cursor-pointer"
                        onClick={() => {
                          // Toggle password visibility for this specific leak
                          setVisiblePasswords(prev => ({
                            ...prev,
                            [leak.id]: !prev[leak.id]
                          }));
                        }}
                      >
                        {visiblePasswords[leak.id] ? leak.password : '•••••••••'}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          navigator.clipboard.writeText(leak.password);
                        }}
                      >
                        <Copy className="h-3 w-3" />
                      </Button>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-300">
                    <div className="flex items-center space-x-2">
                      <span className="truncate max-w-[200px]">{leak.url}</span>
                      {leak.url && (
                        <a
                          href={leak.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-500 hover:text-blue-400"
                        >
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-300">{leak.domainName}</td>
                  <td className="px-6 py-4 text-sm text-gray-300">
                    {currentTeam?.isAdmin ? (
                      <FilterSelect
                        value={leak.twoFaStatus || TwoFaStatusEnum.None}
                        onValueChange={(value) => handleAttributeChange(leak.id, 'twoFaStatus', value)}
                        options={[
                          { value: TwoFaStatusEnum.Enabled, label: 'Enabled', color: get2FAStatusColor(TwoFaStatusEnum.Enabled) },
                          { value: TwoFaStatusEnum.Disabled, label: 'Disabled', color: get2FAStatusColor(TwoFaStatusEnum.Disabled) },
                          { value: TwoFaStatusEnum.None, label: 'None', color: get2FAStatusColor(TwoFaStatusEnum.None) }
                        ]}
                        placeholder="Select 2FA Status"
                        width="w-[100px]"
                        triggerColor={get2FAStatusColor(leak.twoFaStatus)}
                      />
                    ) : (
                      <Badge
                        variant="outline"
                        className={get2FAStatusColor(leak.twoFaStatus as TwoFaStatusEnum)}
                      >
                        {leak.twoFaStatus ? leak.twoFaStatus.charAt(0).toUpperCase() + leak.twoFaStatus.slice(1) : 'None'}
                      </Badge>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-300">
                    {currentTeam?.isAdmin ? (
                      <FilterSelect
                        value={leak.severity || StealerLeakSeverityEnum.High}
                        onValueChange={(value) => handleAttributeChange(leak.id, 'severity', value)}
                        options={[
                          { value: StealerLeakSeverityEnum.Critical, label: 'CRITICAL', color: getSeverityColor(StealerLeakSeverityEnum.Critical)},
                          { value: StealerLeakSeverityEnum.High, label: 'HIGH', color: getSeverityColor(StealerLeakSeverityEnum.High)},
                          { value: StealerLeakSeverityEnum.Medium, label: 'MEDIUM', color: getSeverityColor(StealerLeakSeverityEnum.Medium)},
                          { value: StealerLeakSeverityEnum.Low, label: 'LOW', color: getSeverityColor(StealerLeakSeverityEnum.Low)},
                          { value: StealerLeakSeverityEnum.Info, label: 'INFO', color: getSeverityColor(StealerLeakSeverityEnum.Info)}
                        ]}
                        placeholder="Select Severity"
                        width="w-[100px]"
                        triggerColor={getSeverityColor(leak.severity as StealerLeakSeverityEnum)}
                      />
                    ) : (
                      <Badge
                        variant="outline"
                        className={getSeverityColor(leak.severity as StealerLeakSeverityEnum)}
                      >
                        {leak.severity ? leak.severity.toUpperCase() : 'INFO'}
                      </Badge>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-300">
                    {currentTeam?.isAdmin ? (
                      <FilterSelect
                        value={leak.status || Status111Enum.Open}
                        onValueChange={(value) => handleAttributeChange(leak.id, 'status', value)}
                        options={[
                          { value: Status111Enum.Open, label: 'Open', color: getStatusColor(Status111Enum.Open) },
                          { value: Status111Enum.InProgress, label: 'In Progress', color: getStatusColor(Status111Enum.InProgress) },
                          { value: Status111Enum.Resolved, label: 'Resolved', color: getStatusColor(Status111Enum.Resolved) },
                          { value: Status111Enum.FalsePositive, label: 'False Positive', color: getStatusColor(Status111Enum.FalsePositive) },
                          { value: Status111Enum.Duplicate, label: 'Duplicate', color: getStatusColor(Status111Enum.Duplicate) },
                          { value: Status111Enum.Retest, label: 'Retest', color: getStatusColor(Status111Enum.Retest) }
                        ]}
                        placeholder="Select Status"
                        width="w-[100px]"
                        triggerColor={getStatusColor(leak.status as Status111Enum)}
                      />
                    ) : (
                      <Badge
                        variant="outline"
                        className={getStatusColor(leak.status as Status111Enum)}
                      >
                        {leak.status ? (
                          leak.status === Status111Enum.InProgress ? 'In Progress' : 
                          leak.status === Status111Enum.FalsePositive ? 'False Positive' : 
                          leak.status.charAt(0).toUpperCase() + leak.status.slice(1)
                        ) : 'Open'}
                      </Badge>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-300">
                    {currentTeam?.isAdmin ? (
                      <FilterSelect
                        value={leak.validity || ValidityEnum.New}
                        onValueChange={(value) => handleAttributeChange(leak.id, 'validity', value)}
                        options={[
                          { value: ValidityEnum.New, label: 'New', color: getValidityColor(ValidityEnum.New) },
                          { value: ValidityEnum.Valid, label: 'Valid', color: getValidityColor(ValidityEnum.Valid) },
                          { value: ValidityEnum.Invalid, label: 'Invalid', color: getValidityColor(ValidityEnum.Invalid) }
                        ]}
                        placeholder="Select Validity"
                        width="w-[100px]"
                        triggerColor={getValidityColor(leak.validity as ValidityEnum)}
                      />
                    ) : (
                      <Badge
                        variant="outline"
                        className={getValidityColor(leak.validity as ValidityEnum)}
                      >
                        {leak.validity ? leak.validity.charAt(0).toUpperCase() + leak.validity.slice(1) : 'New'}
                      </Badge>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-300">
                    {new Date(leak.createdAt).toLocaleDateString()}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {!isLoading && leaks.length > 0 && (
        <div className="mt-4">
          <TablePagination
            currentPage={currentPage}
            totalItems={paginationInfo.count}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            paginationInfo={{
              count: paginationInfo.count,
              next: paginationInfo.next,
              previous: paginationInfo.previous,
            }}
          />
        </div>
      )}

      {/* Add Domain Modal */}
      {/* <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="bg-[#1A1F2E] border border-gray-800 rounded-lg p-6 sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-white">
              Add Domain for Stealer Leak Monitoring
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Enter a domain name to monitor for stealer leaks.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <label htmlFor="domain-input" className="block text-sm font-medium text-gray-300 mb-1">
                Domain Name
              </label>
              <input
                id="domain-input"
                type="text"
                value={domainInput}
                onChange={(e) => {
                  setDomainInput(e.target.value);
                  setInputError('');
                }}
                placeholder="example.com"
                className="w-full px-3 py-2 bg-[#0F1629] border border-gray-700 rounded-md text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
              {inputError && (
                <p className="mt-1 text-sm text-red-500">{inputError}</p>
              )}
            </div>
            
            <div>
              <label htmlFor="domain-description" className="block text-sm font-medium text-gray-300 mb-1">
                Description (Optional)
              </label>
              <textarea
                id="domain-description"
                value={domainDescription}
                onChange={(e) => setDomainDescription(e.target.value)}
                placeholder="Enter a description for this domain"
                rows={3}
                className="w-full px-3 py-2 bg-[#0F1629] border border-gray-700 rounded-md text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          
          <DialogFooter className="mt-6 flex justify-end space-x-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setIsModalOpen(false);
                setDomainInput('');
                setDomainDescription('');
                setInputError('');
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => {
                // Validate domain name
                if (!domainInput.trim()) {
                  setInputError('Domain name is required');
                  return;
                }
                
                if (!domainRegex.test(domainInput.trim())) {
                  setInputError('Please enter a valid domain name (e.g., example.com)');
                  return;
                }
                
                // Add the domain with description
                addMonitoredDomain(domainInput.trim(), domainDescription.trim());
              }}
            >
              Add Domain
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog> */}
    </div>
  );
};

export default StealerLeaksList;